import { useState } from 'react';
import { AdminScreen, LoadOrError, adminErrorText, useResource } from './Overview';
import {
  Alert, Button, Card, EmptyState, Icon, Input, Modal, Spinner, StatusPill, useToast
} from '../../components/ui';
import { api } from '../../lib/api';
import { usePageMeta } from '../../lib/seo';
import { tr } from '../../lib/i18n';

interface CategoryRow {
  id: string;
  key: string;
  label: string;
  active: number;
  sort_order: number;
}

interface CategoriesPayload {
  categories: CategoryRow[];
}

export function AdminCategoriesPage(): JSX.Element {
  usePageMeta(tr('Categorias — administração'), tr('Categorias de serviço usadas no cadastro e na busca.'), '/admin/categorias');

  const toast = useToast();
  const resource = useResource<CategoriesPayload>(() => api.get<CategoriesPayload>('/api/admin/categories'));

  const [newKey, setNewKey] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const [labels, setLabels] = useState<Record<string, string>>({});
  const [savingLabel, setSavingLabel] = useState<string | null>(null);
  const [pendingToggle, setPendingToggle] = useState<CategoryRow | null>(null);
  const [toggling, setToggling] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const categories = resource.data?.categories ?? [];

  async function createCategory(): Promise<void> {
    const key = newKey.trim().toLowerCase();
    const label = newLabel.trim();
    if (!/^[a-z0-9_]{2,40}$/.test(key)) {
      setCreateError(tr('A chave deve ter 2 a 40 caracteres, apenas letras minúsculas, números e underline (ex.: nail_design).'));
      return;
    }
    if (label.length < 2 || label.length > 80) {
      setCreateError(tr('O nome exibido deve ter entre 2 e 80 caracteres.'));
      return;
    }
    setCreateError(null);
    setCreating(true);
    try {
      await api.post<{ ok: boolean }>('/api/admin/categories', { key, label });
      toast(`Categoria «${label}» criada. Registrado em Auditoria.`, 'success');
      setNewKey('');
      setNewLabel('');
      await resource.reload();
    } catch (e) {
      setCreateError(adminErrorText(e));
      toast(adminErrorText(e), 'danger');
    } finally {
      setCreating(false);
    }
  }

  async function saveLabel(category: CategoryRow): Promise<void> {
    const value = (labels[category.id] ?? category.label).trim();
    if (value.length < 2 || value.length > 80) {
      toast('O nome exibido deve ter entre 2 e 80 caracteres.', 'danger');
      return;
    }
    setSavingLabel(category.id);
    setActionError(null);
    try {
      await api.patch<{ ok: boolean }>(`/api/admin/categories/${category.id}`, { label: value });
      toast(tr('Nome da categoria atualizado para «{name}».', { name: value }), 'success');
      setLabels((prev) => {
        const next = { ...prev };
        delete next[category.id];
        return next;
      });
      await resource.reload();
    } catch (e) {
      setActionError(adminErrorText(e));
      toast(adminErrorText(e), 'danger');
    } finally {
      setSavingLabel(null);
    }
  }

  async function toggleActive(category: CategoryRow, active: boolean): Promise<void> {
    setToggling(true);
    setActionError(null);
    try {
      await api.patch<{ ok: boolean }>(`/api/admin/categories/${category.id}`, { active });
      toast(
        active
          ? tr('«{name}» voltou a aparecer no cadastro e na busca.', { name: category.label })
          : tr('«{name}» foi desativada: não aparece mais no cadastro de profissionais nem nos filtros de busca.', { name: category.label }),
        'success'
      );
      setPendingToggle(null);
      await resource.reload();
    } catch (e) {
      setActionError(adminErrorText(e));
      toast(adminErrorText(e), 'danger');
    } finally {
      setToggling(false);
    }
  }

  return (
    <AdminScreen
      title={tr("Categorias de serviço")}
      description={tr("Lista usada no cadastro de estabelecimentos e na busca pública. A API permite criar, renomear e ativar/desativar — não existe rota de exclusão, por isso use a desativação.")}
    >
      <Card className="mb-4">
        <h2 className="text-xl">{tr("Nova categoria")}</h2>
        <p className="nl-hint mt-1">{tr("A chave fica gravada no estabelecimento (")}<code>{tr("category_key")}</code>{tr("), então crie com cuidado.")}</p>
        <form
          className="mt-4 flex flex-wrap items-end gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            void createCategory();
          }}
        >
          <div className="min-w-[12rem] flex-1">
            <label className="nl-label" htmlFor="cat-key">{tr("Chave (interna)")}</label>
            <Input
              id="cat-key"
              value={newKey}
              maxLength={40}
              onChange={(e) => setNewKey(e.target.value.toLowerCase())}
              placeholder={tr("nail_design")}
            />
          </div>
          <div className="min-w-[12rem] flex-1">
            <label className="nl-label" htmlFor="cat-label">{tr("Nome exibido")}</label>
            <Input
              id="cat-label"
              value={newLabel}
              maxLength={80}
              onChange={(e) => setNewLabel(e.target.value)}
              placeholder={tr("Nail design")}
            />
          </div>
          <Button type="submit" loading={creating} disabled={creating}>
            <Icon name="plus" /> {tr("Criar categoria")}
          </Button>
        </form>
        {createError ? <div className="mt-3"><Alert tone="danger" title={tr("Não foi possível criar")}>{createError}</Alert></div> : null}
      </Card>

      <LoadOrError loading={resource.loading} error={resource.error} />

      {!resource.loading && categories.length === 0 && !resource.error ? (
        <EmptyState title={tr("Nenhuma categoria cadastrada")} description={tr("Rode o seed da API ou crie a primeira categoria acima.")} />
      ) : null}

      {categories.length > 0 ? (
        <Card className="overflow-x-auto p-0">
          <table className="w-full min-w-[48rem] text-left text-sm">
            <caption className="sr-only">{tr("Categorias de serviço")}</caption>
            <thead className="bg-surface text-muted">
              <tr>
                <th scope="col" className="px-4 py-3 font-semibold">{tr("Ordem")}</th>
                <th scope="col" className="px-4 py-3 font-semibold">{tr("Chave")}</th>
                <th scope="col" className="px-4 py-3 font-semibold">{tr("Nome exibido")}</th>
                <th scope="col" className="px-4 py-3 font-semibold">{tr("Situação")}</th>
                <th scope="col" className="px-4 py-3 text-right font-semibold">{tr("Ações")}</th>
              </tr>
            </thead>
            <tbody>
              {categories.map((category) => {
                const current = labels[category.id] ?? category.label;
                const dirty = current.trim() !== category.label;
                return (
                  <tr key={category.id} className="border-t border-line">
                    <td className="px-4 py-3 tabular-nums">{category.sort_order}</td>
                    <td className="px-4 py-3"><code>{category.key}</code></td>
                    <td className="px-4 py-3">
                      <Input
                        aria-label={`Nome exibido da categoria ${category.key}`}
                        value={current}
                        maxLength={80}
                        disabled={savingLabel === category.id}
                        onChange={(e) => setLabels((prev) => ({ ...prev, [category.id]: e.target.value }))}
                      />
                    </td>
                    <td className="px-4 py-3">
                      {category.active === 1 ? (
                        <StatusPill tone="positive">{tr("Ativa")}</StatusPill>
                      ) : (
                        <StatusPill tone="neutral">{tr("Desativada")}</StatusPill>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="secondary"
                          className="px-3 py-1.5 text-xs"
                          disabled={!dirty || savingLabel !== null}
                          loading={savingLabel === category.id}
                          onClick={() => void saveLabel(category)}
                        >
                          {tr("Salvar nome")}
                        </Button>
                        <Button
                          variant={category.active === 1 ? 'danger' : 'primary'}
                          className="px-3 py-1.5 text-xs"
                          disabled={toggling}
                          onClick={() => {
                            setActionError(null);
                            setPendingToggle(category);
                          }}
                        >
                          {category.active === 1 ? tr('Desativar') : tr('Ativar')}
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      ) : null}

      {actionError ? <div className="mt-4"><Alert tone="danger" title={tr("Ação não concluída")}>{actionError}</Alert></div> : null}

      <Modal
        open={pendingToggle !== null}
        onClose={() => (toggling ? undefined : setPendingToggle(null))}
        title={pendingToggle?.active === 1 ? tr('Desativar categoria') : tr('Ativar categoria')}
        footer={
          <>
            <Button variant="ghost" disabled={toggling} onClick={() => setPendingToggle(null)}>{tr("Voltar")}</Button>
            <Button
              variant={pendingToggle?.active === 1 ? 'danger' : 'primary'}
              loading={toggling}
              disabled={toggling}
              onClick={() => pendingToggle && void toggleActive(pendingToggle, pendingToggle.active !== 1)}
            >
              {pendingToggle?.active === 1 ? tr('Confirmar desativação') : tr('Confirmar ativação')}
            </Button>
          </>
        }
      >
        {pendingToggle ? (
          <div className="space-y-3">
            <Alert tone={pendingToggle.active === 1 ? 'warning' : 'success'} title={tr("Consequência")}>
              {pendingToggle.active === 1
                ? `«${pendingToggle.label}» some do cadastro de novos estabelecimentos e dos filtros de busca pública. Quem já usa esta chave continua com o cadastro intacto — a API não tem rota de exclusão para não quebrar esses registros.`
                : `«${pendingToggle.label}» volta a ser opção no cadastro de estabelecimentos e na busca pública.`}{' '}
              {tr("A alteração é registrada em Auditoria (")}<code>{tr("admin.category_updated")}</code>).
            </Alert>
            {toggling ? <Spinner label={tr("Aplicando")} /> : null}
          </div>
        ) : null}
      </Modal>
    </AdminScreen>
  );
}
